import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

axios.defaults.withCredentials = true;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialer live-session WebSocket. Reuses the call-monitor ws endpoint — the
 * predictive dialer broadcasts dialer.* event types through the same
 * monitor_manager.broadcast_all channel.
 *
 * Messages handled:
 *   - dialer.human_answered  → push to liveEvents for the UI
 *   - active_calls_update    → indicates a status transition (UI re-polls metrics)
 *   - call_status            → per-attempt status transitions
 */
export default function useDialerSocket(campaignId) {
  const [connected, setConnected] = useState(false);
  const [liveEvents, setLiveEvents] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempt = useRef(0);
  const maxReconnect = 10;

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    let token = "";
    try {
      const res = await axios.get(`${API}/api/call-monitor/ws-token`);
      token = res.data.token;
    } catch (e) {
      if (reconnectAttempt.current < maxReconnect) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30000);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      }
      return;
    }

    const base = API || "http://localhost:8001";
    const wsBase = base.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/call-monitor/ws?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
      ws.send(JSON.stringify({ type: "subscribe_dashboard" }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "dialer.human_answered" && msg.campaign_id === campaignId) {
          setLiveEvents((prev) => [...prev.slice(-20), msg]);
        } else if (msg.type === "active_calls_update" || msg.type === "call_status") {
          setLiveEvents((prev) => [...prev.slice(-20), msg]);
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      if (reconnectAttempt.current < maxReconnect) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30000);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      }
    };
    ws.onerror = () => { try { ws.close(); } catch {} };
  }, [campaignId]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectAttempt.current = maxReconnect;
      try { wsRef.current?.close(); } catch {}
      setConnected(false);
    };
  }, [connect]);

  // Ping keep-alive
  useEffect(() => {
    const id = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);
    return () => clearInterval(id);
  }, []);

  return { connected, liveEvents };
}
